// Copyright (c) 2024 WiseTime. All rights reserved.

import { concat, EMPTY, Observable } from "rxjs"
import { Action } from "redux"
import { DateTime } from "luxon"
import { flatten } from "ramda"
import { filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { RootState } from "../../../app/store"
import { notUndefined } from "../../../lib/utils"
import { TimeGroupsCache } from "../types"
import { getBaseUrl } from "../../../app/routing"
import { PostStatus, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

export const pendingResult = (
  timeGroupIdsInPendingProgressCache: TimelineAdditionalUiSlice.TimeGroupIdsInPendingProgressCache,
  timeGroupsCache: TimeGroupsCache,
): {
  pendingProgressCacheKeysToRemove: string[],
  pendingTimeGroups: TimeGroup.AsObject[],
} => {
  const pendingProgressCacheKeysToRemove = Object.keys(timeGroupIdsInPendingProgressCache)
    .filter(key => (DateTime.now().toSeconds() - Number(key)) >= 30)
  return {
    pendingProgressCacheKeysToRemove,
    pendingTimeGroups: flatten(
      pendingProgressCacheKeysToRemove
        .map(key => {
          const { timeGroupsIds = [], postedToSiteId = "" } = timeGroupIdsInPendingProgressCache[key] ?? {}
          return timeGroupsIds.map(timeGroupId => {
            const timeGroup = timeGroupsCache[timeGroupId]
            if (!timeGroup) {
              return undefined
            }
            return {
              ...timeGroup,
              postedToSiteId: timeGroup.postedToSiteId || postedToSiteId,
            }
          })
        })
    )
      .filter(notUndefined)
      .filter(timeGroup => timeGroup.postStatus === PostStatus.PENDING),
  }
}

export const sendSlowTimePostingStatus = (userId: string, entityId: string, teamId: string): void => {
  window.fetch(
    `${getBaseUrl()}/alarm-resolver/api/event`,
    {
      method: "POST",
      headers: { "Content-type": "application/json" },
      body: JSON.stringify({
        group: "slow_time_posting_status",
        userId,
        entityId,
        teamId,
        extraProperties: {},
      })
    },
  )
}

export const checkPendingPostStatus = () =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.checkPendingPostStatus.match),
      withLatestFrom(state$),
      mergeMap(([_, state]) => {
        const {
          pendingProgressCacheKeysToRemove,
          pendingTimeGroups,
        } = pendingResult(
          state.timelineAdditionalUi.timeGroupIdsInPendingProgressCache,
          state.timeline.timeGroupsCache,
        )
        if (pendingProgressCacheKeysToRemove.length) {
          pendingTimeGroups.forEach(timeGroup => {
            sendSlowTimePostingStatus(state.userContext.user?.id ?? "", timeGroup.id, timeGroup.postedToSiteId)
          })
          const refreshActiveDateTimeline = pendingTimeGroups.some(timeGroup => (
            Number(timeGroup.activityDate) === state.timeline.activeDate
          ))
          const actions: Action[] = [
            TimelineAdditionalUiSlice.removeTimeGroupIdsInPendingProgress(pendingProgressCacheKeysToRemove)
          ]
          if (refreshActiveDateTimeline) {
            actions.push(Actions.getCurrentTimelineData())
          }
          return concat(actions)
        }
        return EMPTY
      }),
    )

export default checkPendingPostStatus()
